import {Feature, Map as OLMap, MapBrowserEvent, MapEvent, Overlay, View} from "ol"
import BaseLayer from "ol/layer/Base"
import {AnimationOptions} from "ol/View"
import {Point as OLPoint} from "ol/geom"
import {Extent} from "ol/extent"
import {Size} from "ol/size"
import {Pixel} from "ol/pixel"
import {Coordinate} from "ol/coordinate"
import store from "@ui/stores";
import {
  neighbourLayer,
  positionLayer,
  routeLayer,
  tilesLayer,
  OSMLayer,
  S57Layer,
  averageLayer
} from "@ui/services/common/layers"
import {needRotate} from "@ui/common/utils"
import {extend as extendExtent, createEmpty as createEmptyExtent} from "ol/extent"
import {EventsKey} from "ol/events"
import {unByKey} from "ol/Observable"

export type Point = [number, number]

export interface IViewProps {
  extent: Extent
  minZoom: number
  maxZoom: number
  zoom?: number
  center?: Point
}

export type ZoomChanged = (zoom: number) => void
export type CenterChanged = (center: Point) => void
export type DragMoved = () => void
export type MoveEnd = (event: MapEvent) => void
export type RotationChanged = (rotation: number) => void

export interface IMapService {
  bindMap(props: {
    mapTarget?: HTMLDivElement,
    viewProps: IViewProps
  }): void

  zoomChange(value: number): void

  moveTo(
    point: [number, number],
    props?: {
      duration?: number,
      resolution?: number,
      padding?: [number, number, number, number],
      callback?: (isSucces: boolean) => void
    }
  ): void

  moveToWithRotation(
    point: [number, number],
    rotationData: { rotation: number, anchor?: Coordinate },
    duration?: number): void

  rotate(rotation: number, anchor?: Coordinate): void

  moveToExtent(extent: Extent, duration?: number): void

  getMapSize(): Size | undefined

  getCoordinateFromPixel(pixel: Pixel): Coordinate

  getPixelFromCoordinate(coordinate: Coordinate): Pixel

  getZoom(): number | undefined

  getRotation(): number

  isAnimating(): boolean

  addZoomChangedCallback(callback: ZoomChanged): VoidFunction

  addRotationChangedCallback(callback: RotationChanged): VoidFunction

  addCenterChangedCallback(callback: CenterChanged): VoidFunction

  addDragMovedCallback(callback: DragMoved): void

  addMoveEndCallback(callback: MoveEnd): VoidFunction

  switchWorldLayer(isOSM: boolean): void

  getLayerById<T extends BaseLayer>(id: string): T | undefined

  addLayer<T extends BaseLayer>(layer: T): void

  addOverlay(overlay: Overlay): void

  getResolution(): number | undefined

  getAzimuthResolution(): {pixels: number, meters: number, resolution: number}
}

class MapService implements IMapService {
  #map: OLMap
  #view: View
  #onZoomChangeListeners: Array<{ cb: ZoomChanged, key: EventsKey }> = []
  #onChangeCenterListeners: Array<{ cb: CenterChanged, key: EventsKey }> = []
  #onDragMoved: DragMoved[] = []
  #onMoveEndListeners: Array<{ cb: MoveEnd, key: EventsKey }> = []
  #onRotationChangeListeners: Array<{ cb: RotationChanged, key: EventsKey }> = []

  constructor() {
    this.#view = new View()
    this.#map = new OLMap({
      layers: [
        OSMLayer,
        tilesLayer,
        S57Layer,
        neighbourLayer,
        positionLayer,
        routeLayer,
        averageLayer,
      ],
      controls: [],
    })
  }

  bindMap(props: { mapTarget?: HTMLDivElement, viewProps: IViewProps }) {
    const {mapTarget, viewProps} = props
    this.#map.setTarget(mapTarget)

    const center = viewProps.center || [
      (viewProps.extent[0] + viewProps.extent[2]) / 2,
      (viewProps.extent[1] + viewProps.extent[3]) / 2
    ]
    this.#view.setMinZoom(viewProps.minZoom)
    this.#view.setMaxZoom(viewProps.maxZoom)
    this.#view.setZoom(viewProps.zoom || viewProps.minZoom)
    this.#view.setCenter(center)

    this.#map.setView(this.#view)

    // Отключение contextmenu
    const vp = this.#map.getViewport()
    vp.style.touchAction = "none"
    vp.style.userSelect = "none"
    vp.addEventListener("contextmenu", e => e.preventDefault(), {passive: false})
    vp.addEventListener("pointercancel", e => e.preventDefault(), {passive: true})

    this.#map.on("pointermove", (event) => {
      if (!store.settings.isDesktop) return
      let clickable = false
      this.#map.forEachFeatureAtPixel(
        event.pixel,
        (feature, layer) => {
          if (feature.get("onClick") || feature.get("features")) {
            clickable = true
          }
        }
      )
      this.#map.getTargetElement().style.cursor = clickable ? "pointer" : "default"
    })

    this.#map.on("singleclick", (event) => {
      let features: Feature<OLPoint>[] = (this.#map.getFeaturesAtPixel(event.pixel) || []) as Feature<OLPoint>[]
      const clusterFeatures = ((features[0]?.get("features") || []) as Feature<OLPoint>[])
        .filter(feature => {
          const {type, selected} = feature.getProperties() || {}
          return store.layer.list?.includes(type) || selected
        })

      if (!clusterFeatures?.length) {
        const feature = features.find(f => f.get("onClick"))
        if (feature) {
          feature.get("onClick").call()
        }
        return
      }

      if (clusterFeatures.length > 1 && (this.getZoom() || 0) < (this.#view.getZoomForResolution(1.7) || 16.5)) {
        const extent = clusterFeatures.reduce(
          (ext, feature) => extendExtent(ext, feature.getGeometry()?.getExtent() || []),
          createEmptyExtent()
        )
        this.#view.fit(extent, {duration: 600, padding: [400, 100, 100, 100]})
      } else {
        const [clickLng, clickLat] = this.getCoordinateFromPixel(event.pixel)
        const {feature} = clusterFeatures.map(feature => {
          const [lng, lat] = feature.getGeometry()?.getCoordinates() || []
          return {
            distance: azimuth({lng: lng, lat: lat},{lng: clickLng, lat: clickLat}).distance,
            feature
          } as {distance: number, feature: Feature<OLPoint>}
        }).reduce((
          min,
          feature
          ) => feature.distance < min.distance ? feature : min
        )
        feature.get("onClick")?.call()
      }
    })

  }

  addZoomChangedCallback(callback: ZoomChanged): VoidFunction {
    const key: EventsKey = this.#view.on("change:resolution", (event) => {
      const zoom = this.#view.getZoom()
      zoom && callback(zoom)
    })
    this.#onZoomChangeListeners.push({cb: callback, key})

    return () => {
      unByKey(key)
      const idx = this.#onZoomChangeListeners.findIndex(cb => cb.key===key)
      if (idx >= 0) {
        this.#onZoomChangeListeners.splice(idx, 1)
      }
    }
  }

  addRotationChangedCallback(callback: RotationChanged): VoidFunction {
    const key: EventsKey = this.#view.on("change:rotation", () => {
      const rotation = this.#view.getRotation()
      rotation && callback(rotation)
    })
    this.#onRotationChangeListeners.push({cb: callback, key})

    return () => {
      unByKey(key)
      const idx = this.#onRotationChangeListeners.findIndex(cb => cb.key===key)
      if (idx >= 0) {
        this.#onRotationChangeListeners.splice(idx, 1)
      }
    }
  }

  addCenterChangedCallback(callback: CenterChanged): VoidFunction {
    const key: EventsKey = this.#view.on("change:center", () => {
      const center = this.#view.getCenter()
      center && callback([center[0], center[1]])
    })
    this.#onChangeCenterListeners.push({cb: callback, key})

    return () => {
      unByKey(key)
      const idx = this.#onChangeCenterListeners.findIndex(cb => cb.key===key)
      if (idx >= 0) {
        this.#onChangeCenterListeners.splice(idx, 1)
      }
    }
  }

  addDragMovedCallback(callback: DragMoved): void {
    this.#onDragMoved.push(callback)
  }

  addMoveEndCallback(callback: MoveEnd): VoidFunction {
    const key: EventsKey = this.#map.on("moveend", (event: MapEvent) => {
      callback(event)
    })
    this.#onMoveEndListeners.push({cb: callback, key})

    return () => {
      unByKey(key)
      const idx = this.#onMoveEndListeners.findIndex(cb => cb.key===key)
      if (idx >= 0) {
        this.#onMoveEndListeners.splice(idx, 1)
      }
    }
  }

  zoomChange(value: number): void {
    const zoom = this.#view.getZoom()
    zoom && this.#view.animate({zoom: zoom + value, duration: 250})
  }

  moveTo(
    point: [number, number],
    props?: {
      duration?: number,
      resolution?: number,
      padding?: [number, number, number, number],
      callback?: (isSucces: boolean) => void
    }
  ): void {
    const [width, height] = this.getMapSize() || []
    const [top, right, bottom, left] = props?.padding || [0.5, 0.5, 0.5, 0.5]
    this.#view.fit(
      new OLPoint(point), {
        padding: [height * top, width * right, height * bottom, width * left],
        minResolution: props?.resolution || this.#view.getResolution(),
        duration: props?.duration || 600,
        callback: props?.callback
      }
    )
  }

  moveToWithRotation(
    point: [number, number],
    rotationData: { rotation: number, anchor?: Coordinate },
    duration?: number
  ): void {
    const {rotation, anchor} = rotationData
    let data: AnimationOptions = {center: point, duration}
    if (needRotate(rotation, this.#view.getRotation())) data = {...data, rotation, anchor}
    this.#view.animate(data)
  }

  rotate(rotation: number, anchor?: Coordinate) {
    this.#view.animate({rotation, anchor})
  }

  moveToExtent(extent: Extent, duration: number = 600): void {
    this.#view.fit(
      extent, {
        padding: [10, 10, 10, 10],
        duration: duration,
      }
    )
  }

  getMapSize() {
    return this.#map.getSize()
  }

  getCoordinateFromPixel(pixel: Pixel): Coordinate {
    return this.#map.getCoordinateFromPixel(pixel)
  }

  getPixelFromCoordinate(coordinate: Coordinate): Pixel {
    return this.#map.getPixelFromCoordinate(coordinate)
  }

  getZoom(): number | undefined {
    return this.#view.getZoom()
  }

  getRotation(): number {
    return this.#view.getRotation()
  }

  isAnimating(): boolean {
    return this.#view.getAnimating()
  }

  switchWorldLayer(isOSM: boolean) {
    S57Layer.setVisible(!isOSM)
    OSMLayer.setVisible(isOSM)
    tilesLayer.setVisible(isOSM)
  }

  getLayerById<T extends BaseLayer>(id: string): T | undefined {
    if (!id) return
    for (let layer of this.#map.getLayers().getArray()) {
      if (layer && layer.getProperties()?.id === id) {
        return layer as T
      }
    }
  }

  addLayer<T extends BaseLayer>(layer: T) {
    this.#map.addLayer(layer)
  }

  addOverlay(overlay: Overlay) {
    this.#map.addOverlay(overlay)
  }

  getResolution(): number | undefined {
    return this.#map.getView().getResolution()
  }

  getAzimuthResolution() {
    const [width, height] = this.getMapSize() || []
    const pixels = Math.max(width, height)
    const [Alng, Alat] = this.getCoordinateFromPixel([0, 0])
    const [Blng, Blat] = this.getCoordinateFromPixel([width, 0])
    const [Clng, Clat] = this.getCoordinateFromPixel([0, height])
    const widthInMeters = azimuth({lng: Alng, lat: Alat},{lng: Blng, lat: Blat}).distance
    const heightInMeters = azimuth({lng: Alng, lat: Alat},{lng: Clng, lat: Clat}).distance
    const meters = Math.max(widthInMeters, heightInMeters)
    return {meters, pixels, resolution: meters / pixels}
  }
}

export default new MapService() as IMapService
